/**
 * ============================================================================
 * FOLLOW PAGINATION CHAIN — shared streaming page loop for the deep-dive
 * accessors (spec dive-deep-dcr-so, design "Architecture").
 * ============================================================================
 *
 * LAYER: accessor helper. Owns the transport-level pagination protocol the
 * same way GetMetricsApi does — follow paginationKey until absent, guard
 * against runaway key loops — with one addition: an onPage callback fires as
 * each page lands, so the RTK endpoint can stream rows into its cache entry
 * (first page renders while the rest load; spec R2.2).
 *
 * A mid-chain failure throws PaginationChainError carrying the last GOOD
 * cursor, so the caller can keep delivered pages and resume (spec R2.4).
 */

import { recordPaginationChain } from 'src/metrics/performanceMetrics';

/**
 * Follow-the-key page limit. Today's prod data needs 19 (DCR) / 12 (SO)
 * pages at pageSize 100; exceeding this indicates a runaway key loop, not
 * real data.
 */
export const MAX_SUMMARY_PAGES = 30;

/** One page of a chain: its rows plus the envelope's continuation key. */
export interface ChainPage<R> {
  rows: R[];
  paginationKey?: string;
  lastRefreshedAt: string;
}

/** Thrown on a mid-chain page failure; resumeKey re-enters where it stopped. */
export class PaginationChainError extends Error {
  public readonly resumeKey?: string;
  public readonly pagesDelivered: number;
  public readonly cause: unknown;

  constructor(cause: unknown, resumeKey: string | undefined, pagesDelivered: number) {
    // Fold the cause in: the endpoint surfaces only error.message, and the
    // real reason (transport fault, stuck key) must reach the user and logs.
    super(
      `pagination chain failed after ${pagesDelivered} page(s): ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
    );
    this.name = 'PaginationChainError';
    this.cause = cause;
    this.resumeKey = resumeKey;
    this.pagesDelivered = pagesDelivered;
  }
}

/**
 * Follows a page chain serially (page N+1 needs page N's token), invoking
 * onPage per page. Resolves when the chain terminates. `startKey` resumes a
 * previously failed chain (spec R2.4: completed pages are never re-requested);
 *
 * The perf beacon fires only on natural termination, per
 * recordPaginationChain's contract (partial chains skew Chain.Pages downward;
 * the failing page is already counted by Api.Fault) — same as
 * getMetricsAllPages. Each invocation reports its OWN pages and duration, so
 * a resumed chain emits two self-consistent beacons rather than one mixing
 * a cumulative count with a partial duration.
 */
export async function followPaginationChain<R>(
  operation: 'GetDcrSummary' | 'GetDcrServiceOrderSummary',
  fetchPage: (paginationKey?: string) => Promise<ChainPage<R>>,
  onPage: (rows: R[], pageNo: number, lastRefreshedAt: string) => void,
  startKey?: string,
): Promise<void> {
  let paginationKey = startKey;
  let pagesDelivered = 0;
  const chainStartedAt = performance.now();

  for (let pageNo = 0; pageNo < MAX_SUMMARY_PAGES; pageNo++) {
    const requestedKey = paginationKey;
    let page: ChainPage<R>;
    try {
      page = await fetchPage(requestedKey);
    } catch (error) {
      throw new PaginationChainError(error, requestedKey, pagesDelivered);
    }
    onPage(page.rows, pageNo, page.lastRefreshedAt);
    pagesDelivered++;
    // A server echoing back the key it was just handed is a stuck loop —
    // fail on the FIRST repeat, before another duplicate page streams into
    // the UI (MAX_SUMMARY_PAGES remains the backstop for rotating loops).
    if (page.paginationKey && page.paginationKey === requestedKey) {
      throw new PaginationChainError(
        new Error(`${operation} returned an unchanged paginationKey`),
        // A stuck key has no valid resume point (its page was already
        // delivered) — undefined forces the clean-restart path.
        undefined,
        pagesDelivered,
      );
    }
    paginationKey = page.paginationKey;
    if (!paginationKey) {
      recordPaginationChain(operation, pagesDelivered, performance.now() - chainStartedAt);
      return;
    }
  }

  // Overrun: with the repeated-key fast-fail above, reaching the cap means a
  // rotating key loop OR the data genuinely outgrew the cap. Carry the
  // cursor so Resume continues from page MAX+1 instead of restarting.
  throw new PaginationChainError(
    new Error(`${operation} pagination did not terminate within ${MAX_SUMMARY_PAGES} pages`),
    paginationKey,
    pagesDelivered,
  );
}
